import { useState } from "react";
import { PROPERTY_TYPE_LABEL_BY_ID } from "../constants/propertyTypes";

/**
 * Traveler-facing enrichment blocks rendered on every override card
 * surface (Explore CardFace, DetailModal, Bookmarks, StaySearch).
 * Cardinal Rule: never used on Atlas.
 *
 * All sub-components short-circuit to `null` when their input is empty
 * so the card layout stays clean for overrides that only fill a subset
 * of the enrichment fields.
 */

export function DescriptionPreview({ text, maxChars = 140, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const clean = String(text);
  if (clean.length <= maxChars) {
    return (
      <p data-testid={`${testIdPrefix}-desc`}
         className="mt-1 text-[12px] leading-snug text-tb-navy/75 whitespace-pre-wrap">
        {clean}
      </p>
    );
  }
  return (
    <p data-testid={`${testIdPrefix}-desc`}
       className="mt-1 text-[12px] leading-snug text-tb-navy/75 whitespace-pre-wrap">
      {open ? clean : clean.slice(0, maxChars).trimEnd() + "…"}
      <button type="button"
              onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
              data-testid={`${testIdPrefix}-desc-toggle`}
              className="ml-1 font-semibold text-tb-navy underline underline-offset-2">
        {open ? "show less" : "show more"}
      </button>
    </p>
  );
}

export function TipBadge({ label, text, testId }) {
  if (!text) return null;
  return (
    <div data-testid={testId}
         className="mt-2 rounded-md border border-tb-navy/15 bg-tb-cream-2/60 px-2.5 py-1.5">
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-tb-navy/60 font-hand">
        {label}
      </div>
      <p className="mt-0.5 text-[12px] leading-snug text-tb-navy/85 whitespace-pre-wrap">
        {text}
      </p>
    </div>
  );
}

export function TipStack({ override, testIdPrefix }) {
  if (!override) return null;
  return (
    <>
      <TipBadge label="Advisor Tip"  text={override.advisor_tip}
                testId={`${testIdPrefix}-advisor-tip`} />
      <TipBadge label="Traveler Tip" text={override.traveler_tip}
                testId={`${testIdPrefix}-traveler-tip`} />
    </>
  );
}

export function FilterPillBadges({ pills, testId }) {
  const list = (pills || []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <div data-testid={testId} className="mt-3 flex flex-wrap gap-1.5">
      {list.map((id) => (
        <span key={id}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-tb-navy/[0.06] border border-tb-navy/15 text-[10px] font-hand text-tb-navy/80">
          {PROPERTY_TYPE_LABEL_BY_ID[id] || id}
        </span>
      ))}
    </div>
  );
}

/**
 * Gallery for the detail modal — swipeable if >1 photo, single image
 * otherwise.  Falls back to `null` when photos empty (caller renders
 * a placeholder instead).
 */
export function OverridePhotoGallery({ photos, name, testIdPrefix }) {
  const list = (photos || []).filter((p) => p && p.url);
  const [idx, setIdx] = useState(0);
  if (list.length === 0) return null;
  const cur = list[Math.min(idx, list.length - 1)];
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden bg-tb-navy/5"
         data-testid={`${testIdPrefix}-gallery`}>
      <img src={cur.url} alt={name || ""}
           className="absolute inset-0 w-full h-full object-cover" />
      {list.length > 1 && (
        <>
          <button type="button"
                  onClick={(e) => { e.stopPropagation(); setIdx((i) => Math.max(0, i - 1)); }}
                  data-testid={`${testIdPrefix}-gallery-prev`}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 text-tb-navy grid place-items-center">‹</button>
          <button type="button"
                  onClick={(e) => { e.stopPropagation(); setIdx((i) => Math.min(list.length - 1, i + 1)); }}
                  data-testid={`${testIdPrefix}-gallery-next`}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/85 text-tb-navy grid place-items-center">›</button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {list.map((_, i) => (
              <span key={i}
                    className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-white" : "bg-white/50"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
